import React from 'react';
import { noteTemplateFor } from '../model/noteTemplates';
import { Entry } from '../types';
import { codeLabel } from '../model/entryMeta';
import { lateEntryLabel } from '../../../../utils/observedAt';
import { formatStaff, StaffMember, useStaffDirectoryContext } from '../StaffDirectoryContext';
import { useChartForm } from '../ChartFormContext';
import {
  IncidentExtras,
  MarContent,
  NoteContent,
  OutputContent,
  RenderContext,
  RespiratoryContent,
  SeizureContent,
  SupervisoryContent, TherapyContent, AideTasksContent, TemplateNoteContent,
  VitalsContent,
  WoundContent
} from './EntryRenderers';

interface EntryContentProps {
  entry: Entry;
}

const EntryContent: React.FC<EntryContentProps> = ({ entry }) => {
  // Phase 18 M5: the same resolver names the invalidating user, the author (EntryListItem) and supervised staff
  const directory = useStaffDirectoryContext();
  const chart = useChartForm();
  const ctx: RenderContext = { visitEntries: chart?.visitEntries, resolveStaff: directory?.resolve };
  const [showOriginal, setShowOriginal] = React.useState(false);
  // The first saved version is the original text (versions are kept oldest first)
  const original = entry.versions && entry.versions.length > 0 ? entry.versions[0] : null;
  // Function to render the content based on entry type
  const renderContent = () => {
    switch (entry.type) {
      case 'vitals':
        return entry.data && typeof entry.data === 'object' ? <VitalsContent data={entry.data} /> : <div className="text-sm">{String(entry.data ?? '')}</div>;

      case 'measurements': {
        const data = entry.data as {
          height: string;
          heightUnit: string;
          weight: string;
          weightUnit: string;
          headCircumference: string;
          headCircumferenceUnit: string;
        };
        
        return (
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-gray-500">Height</div>
              <div>{data.height} {data.heightUnit}</div>
            </div>
            <div>
              <div className="text-gray-500">Weight</div>
              <div>{data.weight} {data.weightUnit}</div>
            </div>
            <div>
              <div className="text-gray-500">Head Circumference</div>
              <div>{data.headCircumference} {data.headCircumferenceUnit}</div>
            </div>
          </div>
        );
      }
      
      // Summary entries
      case 'summary': {
        const text = entry.data?.note || entry.data?.situation || entry.data?.assessment || '';
        return (
          <div className="text-sm whitespace-pre-wrap text-gray-700">
            {text || <span className="text-gray-400 italic">No content</span>}
          </div>
        );
      }

      // Supervisor addendum (Phase 9C)
      case 'addendum': {
        return (
          <div className="text-sm whitespace-pre-wrap text-gray-700" data-testid="addendum-content">
            {entry.data?.note || <span className="text-gray-400 italic">No content</span>}
          </div>
        );
      }

      // Incident entries
      case 'incident': {
        const severity = typeof entry.data?.severity === 'string' ? entry.data.severity : '';
        return (
          <div className="space-y-1">
            {(severity || entry.data?.incidentType) && (
              <div className="flex flex-wrap items-center gap-1.5">
                {severity && (
                  <span data-testid="incident-severity" className={`rounded border px-1.5 py-0.5 text-xs font-semibold ${SEVERITY_STYLES[severity] || SEVERITY_STYLES.LOW}`}>
                    {codeLabel(severity)} severity
                  </span>
                )}
                {entry.data?.incidentType && <span className="text-xs font-medium text-gray-700">{codeLabel(String(entry.data.incidentType))}</span>}
              </div>
            )}
            <div className="text-sm whitespace-pre-wrap text-gray-700">
              {entry.data?.description || (typeof entry.data === 'string' ? entry.data : '') || <span className="text-gray-400 italic">No description</span>}
            </div>
            {entry.data && typeof entry.data === 'object' && <IncidentExtras data={entry.data} />}
          </div>
        );
      }

      // Phase 18 M8 / F21
      case 'respiratory':
        return entry.data && typeof entry.data === 'object' ? <RespiratoryContent data={entry.data} /> : null;
      case 'seizure':
        return entry.data && typeof entry.data === 'object' ? <SeizureContent data={entry.data} ctx={ctx} /> : null;
      case 'output':
        return entry.data && typeof entry.data === 'object' ? <OutputContent data={entry.data} /> : null;
      case 'supervisory':
        return entry.data && typeof entry.data === 'object' ? <SupervisoryContent data={entry.data} ctx={ctx} /> : null;
      case 'therapy':
        return entry.data && typeof entry.data === 'object' ? <TherapyContent data={entry.data} /> : null;
      case 'aidetasks':
        return entry.data && typeof entry.data === 'object' ? <AideTasksContent data={entry.data} /> : null;

      // For text-based entries like notes, mar, etc.
      default: {
        // Phase 20E: note templates
        const template = noteTemplateFor(entry.type);
        if (template && entry.data && typeof entry.data === 'object') return <TemplateNoteContent template={template} data={entry.data} />;
        // If data is a string, display it directly
        if (typeof entry.data === 'string') {
          return <div className="text-sm">{entry.data}</div>;
        }

        // For data that has a specific structure but we want to display as text
        if (entry.data && typeof entry.data === 'object') {
          // Handle specific object structures based on entry types
          if (entry.type === 'notes' && entry.data.note) {
            return <NoteContent data={entry.data} />;
          }
          if (entry.type === 'mar') {
            if (entry.data.medicationName) return <MarContent entry={entry} ctx={ctx} />;
            if (entry.data.note) {
              return <div className="text-sm">{entry.data.note}</div>;
            }
          }
          if (entry.type === 'feed' && (entry.data.feedType || entry.data.volumeMl !== undefined || entry.data.tolerance || entry.data.rateMlPerHour !== undefined)) {
            const d = entry.data;
            return (
              <div className="text-sm space-y-0.5" data-testid="feed-content">
                {d.feedType && <div><span className="font-medium">Type:</span> {codeLabel(d.feedType)}{d.formula ? ` · ${d.formula}` : ''}</div>}
                {(d.volumeMl !== undefined || d.durationMinutes !== undefined) && (
                  <div>
                    <span className="font-medium">Amount:</span>
                    {d.volumeMl !== undefined ? ` ${d.volumeMl} mL` : ''}
                    {d.durationMinutes !== undefined ? ` over ${d.durationMinutes} min` : ''}
                  </div>
                )}
                {d.rateMlPerHour !== undefined && <div><span className="font-medium">Rate:</span> {d.rateMlPerHour} mL/h</div>}
                {d.residualMl !== undefined && <div><span className="font-medium">Residual:</span> {d.residualMl} mL</div>}
                {d.tolerance && <div><span className="font-medium">Tolerance:</span> {codeLabel(d.tolerance)}</div>}
                {(d.notes || d.feedDetails) && <div><span className="font-medium">Notes:</span> {d.notes || d.feedDetails}</div>}
              </div>
            );
          }
          if (entry.type === 'feed' && entry.data.feedDetails) {
            return <div className="text-sm">{entry.data.feedDetails}</div>;
          }
          if (entry.type === 'wound' && (entry.data.woundDescription || entry.data.woundLocation)) {
            return <WoundContent data={entry.data} />;
          }

          // Fallback for unknown object structures - convert to JSON string
          return <div className="text-sm whitespace-pre-wrap">{JSON.stringify(entry.data, null, 2)}</div>;
        }

        // If data is null or undefined
        return <div className="text-sm text-gray-500">No content available</div>;
      }
    }
  };

  return (
    <div>
      {entry.flagged && (
        <div className="bg-amber-100 border-l-4 border-amber-500 p-2 mb-3 text-xs">
          This entry has been flagged for review.
        </div>
      )}
      
      {entry.invalidated && (
        <div
          className="bg-red-100 border-l-4 border-red-500 p-2 mb-3 text-xs text-red-800"
          data-testid="entry-invalidated-banner"
        >
          {describeInvalidation(entry, directory?.resolve)}
        </div>
      )}
      
      <IntegrityBadges entry={entry} showOriginal={showOriginal} onToggleOriginal={() => setShowOriginal((v) => !v)} />

      {entry.invalidated ? (
        <div className="line-through text-gray-500 opacity-80" data-testid="entry-invalidated-content">
          {renderContent()}
        </div>
      ) : (
        renderContent()
      )}

      {showOriginal && original && (
        <div className="mt-2 rounded border border-dashed border-gray-300 bg-gray-50 p-2" data-testid="entry-original-version">
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
            Original (version {original.version}{original.savedAt ? `, ${formatInvalidatedAt(original.savedAt)}` : ''}) · read-only
          </div>
          <EntryContent entry={{ ...entry, data: original.data, amended: false, versions: [], lateEntry: false, flagged: false, invalidated: false }} />
        </div>
      )}
    </div>
  );
};

const SEVERITY_STYLES: Record<string, string> = {
  LOW: 'border-gray-300 bg-gray-100 text-gray-700',
  MODERATE: 'border-amber-300 bg-amber-100 text-amber-800',
  HIGH: 'border-red-300 bg-red-100 text-red-800',
  CRITICAL: 'border-red-700 bg-red-600 text-white'
};

/** Late, amended and addendum badges (Phase 9C) */
const IntegrityBadges: React.FC<{ entry: Entry; showOriginal: boolean; onToggleOriginal: () => void }> = ({ entry, showOriginal, onToggleOriginal }) => {
  const hasOriginal = Boolean(entry.amended && entry.versions && entry.versions.length > 0);
  const unsynced = entry.syncState && entry.syncState !== 'synced';
  if (!entry.lateEntry && !entry.amended && !entry.addendum && !unsynced) return null;
  const minutes = typeof entry.minutesAfterEnd === 'number' ? entry.minutesAfterEnd : null;
  return (
    <div className="mb-1 flex flex-wrap items-center gap-1.5 text-xs">
      {unsynced && (
        <span data-testid="not-synced-badge" className="rounded border border-amber-400 bg-amber-100 px-1.5 py-0.5 font-medium text-amber-900">
          {entry.syncState === 'conflict' ? 'Conflict — open Sync Center' : entry.syncState === 'needs_attention' ? 'Needs attention — open Sync Center' : 'Not synced'}
        </span>
      )}
      {entry.addendum && (
        <span data-testid="addendum-badge" className="rounded border border-indigo-300 bg-indigo-50 px-1.5 py-0.5 font-medium text-indigo-800">
          Supervisor addendum{entry.addendum.refEntryId ? ' · linked to an entry' : ''}
        </span>
      )}
      {entry.lateEntry && (
        <span data-testid="late-entry-badge" className="rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 font-medium text-amber-800">
          {entry.documentedAt
            ? lateEntryLabel(entry.documentedAt, entry.observedAt)
            : `Late entry${minutes !== null ? ` · ${formatLateness(minutes)} after visit end` : ''}`}
        </span>
      )}
      {entry.amended && (
        <span data-testid="amended-badge" className="rounded border border-sky-300 bg-sky-50 px-1.5 py-0.5 font-medium text-sky-800"
          title={entry.lastAmendment?.reason ? `Reason: ${entry.lastAmendment.reason}` : undefined}>
          Amended{entry.version ? ` · v${entry.version}` : ''}
          {entry.lastAmendment?.at ? ` · ${formatInvalidatedAt(entry.lastAmendment.at)}` : ''}
        </span>
      )}
      {entry.amended && entry.lastAmendment?.reason && (
        <span className="text-gray-600">Reason: {entry.lastAmendment.reason}</span>
      )}
      {hasOriginal && (
        <button type="button" onClick={onToggleOriginal} className="font-medium text-sky-700 underline hover:text-sky-900">
          {showOriginal ? 'Hide original' : 'View original'}
        </button>
      )}
    </div>
  );
};

/** 95 -> "1 h 35 min" */
const formatLateness = (minutes: number): string => {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
};

/** Format an ISO time as "Oct 7, 2026, 2:05 PM"; returns the input when it cannot be parsed. */
const formatInvalidatedAt = (value: string): string => {
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * "Invalidated by {name or id} on {date}: {reason}", leaving out whatever is missing.
 * Phase 18 M5: a user id is shown as "Name, credentials" when the staff directory knows it.
 */
export const describeInvalidation = (entry: Entry, resolve?: (userId: string | null | undefined) => StaffMember | undefined): string => {
  const info = entry.invalidationInfo;
  const who = info?.byName || formatStaff(resolve?.(info?.by)) || info?.by;
  let text = 'Invalidated';
  if (who) text += ` by ${who}`;
  if (info?.at) text += ` on ${formatInvalidatedAt(info.at)}`;
  if (info?.reason) text += `: ${info.reason}`;
  return text === 'Invalidated' ? 'This entry has been invalidated.' : text;
};

export default EntryContent;
